import { Person } from "@/views/cases/data/schema";
import api_base, { serializeParams } from "./base";

export interface CalendarEvent {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  color: Colors;
  text_classes: string;
  originalData: Meeting;
}

interface Colors {
  backgroundColor: string
  borderColor: string
}

export interface CalendarDay {
  day: number | null;
  date: Date | null;
  events: CalendarEvent[];
  hasEvent: boolean;
}

export interface CaseR {
  id: number;
  name: string;
}

export interface MeetingList {
  next: number | null
  previous: number | null
  results: Meeting[],
  count: number
}

export interface Meeting {
  id: string;
  added_by: Person;
  assigned_to: Person;
  desc?: string;
  case: CaseR;
  tribunal_proc_type?: boolean;
  created_at: number;
  start_time: number;
  end_time: number | null;
  is_postponed?: boolean;
  postponed_from?: {
    id: string;
    start_time: number;
    end_time: number | null;
  };
  _originalMeetingId?: string;
  tribunal?: string;
  client_name?: string;
}

export interface CreateMeetingRequest {
  assigned_to: string;
  desc: string;
  case: number;
  tribunal_proc_type?: boolean;
  start_time: number;
  end_time: number | null;
}

export interface MeetingQueryParams {
  month?: number;
  year?: number;
  day?: number;
  case_types?: string[];
  assigned_to?: string;
}

export const meetingService = {
  getMeetings: async (
    month: number,
    year: number,
    day?: number,
    case_types?: string[],
    assigned_to?: string,
  ): Promise<Meeting[]> => {
    const response = await api_base.get(`/api/v1/rendezvous/list`, {
      params: {
        month,
        year,
        day,
        case_types,
        assigned_to,
      },
      paramsSerializer: params => serializeParams(params)
    });
    return response.data;
  },

  getMeetingsPaginated: async ({
    start,
    end,
    assigned_to,
    page,
    page_size
  }: {
    start: number;
    end?: number;
    assigned_to?: string;
    page?: number;
    page_size?: number;
  }): Promise<MeetingList> => {
    const response = await api_base.get(`/api/v1/rendezvous/list`, {
      params: {
        start,
        end,
        assigned_to,
        page,
        page_size
      },
      paramsSerializer: params => serializeParams(params)
    });
    return response.data;
  },

  createMeeting: async (data: CreateMeetingRequest): Promise<Meeting> => {
    const response = await api_base.post(`/api/v1/rendezvous/create`, data);
    return response.data;
  },

  updateMeeting: async (
    id: string,
    data: Partial<CreateMeetingRequest>
  ): Promise<Meeting> => {
    const response = await api_base.patch(
      `/api/v1/rendezvous/update/${id}`,
      data
    );
    return response.data;
  },

  postponeMeeting: async (
    id: string,
    data: Partial<CreateMeetingRequest>
  ): Promise<Meeting> => {
    const response = await api_base.post(
      `/api/v1/rendezvous/postpone/${id}`,
      data
    );
    return response.data;
  },

  deleteMeeting: async (id: string): Promise<void> => {
    await api_base.delete(`/api/v1/rendezvous/remove/${id}`);
  },
};
